import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";

export type NewsFilterKey = "groupings" | "sources";

/**
 * News filter state, kept in the URL as repeated search params
 * (?groupings=skatt&sources=DIB) so the sidebar (in DashboardLayout) and the
 * page (in the Outlet) share it without prop plumbing, and filtered views are
 * deep-linkable. `groupings` holds portal-grouping slugs (see
 * useNewsGroupings). Values are returned sorted so query cache keys are
 * stable regardless of click order.
 */
export function useNewsFilters() {
    const [searchParams, setSearchParams] = useSearchParams();

    const groupings = useMemo(
        () => [...searchParams.getAll("groupings")].sort(),
        [searchParams]
    );
    const sources = useMemo(
        () => [...searchParams.getAll("sources")].sort(),
        [searchParams]
    );

    const setValues = useCallback(
        (key: NewsFilterKey, values: string[]) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete(key);
                    for (const value of values) next.append(key, value);
                    return next;
                },
                {replace: true}
            );
        },
        [setSearchParams]
    );

    const toggleValue = useCallback(
        (key: NewsFilterKey, value: string) => {
            setSearchParams(
                (prev) => {
                    const current = prev.getAll(key);
                    const next = new URLSearchParams(prev);
                    next.delete(key);
                    const toggled = current.includes(value)
                        ? current.filter((v) => v !== value)
                        : [...current, value];
                    for (const v of toggled) next.append(key, v);
                    return next;
                },
                {replace: true}
            );
        },
        [setSearchParams]
    );

    const clear = useCallback(
        (key: NewsFilterKey) => setValues(key, []),
        [setValues]
    );

    return {groupings, sources, setValues, toggleValue, clear};
}
